/**
 * Copy text to the clipboard, synchronously where the browser allows it.
 *
 * Order is deliberately the OPPOSITE of the Next.js `src/utils/clipboard.ts`,
 * which tries `navigator.clipboard.writeText` first and awaits it. That is fine
 * in React, where the copy is the last thing that happens. Here the caller
 * copies and then immediately opens a tab, and an `await` ends the user-gesture
 * task — so the popup gets blocked. `execCommand` is synchronous and keeps the
 * gesture intact, which is why the .in engine reaches for it first too.
 *
 * The Clipboard API is still used, as a fallback and fire-and-forget: by then
 * the gesture is already spent either way.
 *
 * Returns true if the synchronous path worked, or if the async one was started.
 *
 * `options.confirmed` narrows that to "the clipboard definitely has the text":
 * only the synchronous `execCommand` result counts, and `writeText` is still
 * fired but no longer reported as success. The CID page's auto-copy needs this.
 * It runs WITHOUT a user gesture, where `execCommand` is refused and
 * `writeText` is started only to reject a tick later — and the default return
 * value would have the page announce "Copied" over an empty clipboard.
 * A caller inside a real gesture wants the default: there, an optimistic true
 * is almost always right, and it is the only answer available synchronously.
 */
export function copyToClipboard(text, options) {
  if (!text) return false;

  if (execCommandCopy(text)) return true;

  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    // Not awaited — see above. A rejection here means the browser refused the
    // copy, which is not something the visitor can act on.
    navigator.clipboard.writeText(text).catch(() => {});
    return !(options && options.confirmed);
  }

  return false;
}

function execCommandCopy(text) {
  if (typeof document === 'undefined' || !document.execCommand) return false;

  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'absolute';
  area.style.left = '-9999px';
  // Anchored to the current scroll position: `position: fixed; top: 0` makes
  // iOS scroll the page to the element before selecting it.
  area.style.top = `${window.scrollY || document.documentElement.scrollTop || 0}px`;
  area.style.opacity = '0';
  area.style.pointerEvents = 'none';
  document.body.appendChild(area);

  let copied = false;
  try {
    // iOS Safari ignores .select() on a readonly textarea and needs an explicit
    // range plus setSelectionRange — the same dance both source apps do.
    if (/ipad|iphone|ipod/i.test(navigator.userAgent)) {
      area.contentEditable = 'true';
      area.readOnly = false;
      const range = document.createRange();
      range.selectNodeContents(area);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      area.setSelectionRange(0, 999999);
    } else {
      area.select();
    }
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  area.remove();
  return copied;
}
